import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../context/AuthContext';
import { useCypherChallenge } from '../hooks/useCypherChallenge';
import { CYPHER_COPY, CYPHER_STEPS } from '../config/cypherChallenge';
import CypherTrackCard from '../components/cypher/CypherTrackCard';
import VerseCard from '../components/verses/VerseCard';
import './CypherChallenge.css';

const CypherChallenge: React.FC = () => {
  const { authState } = useAuth();
  const { beats, verses, loading, error, refresh } = useCypherChallenge();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const role = authState.user?.role;

  return (
    <div className={`cypher-page ${ready ? 'cypher-page--ready' : ''}`}>
      <Helmet>
        <title>DHH Collab | Cypher Challenge</title>
        <meta
          name="description"
          content="Join the DHH Collab Cypher Challenge — drop bars over featured beats and stack the session with rappers across India."
        />
      </Helmet>

      <div className="cypher-page__bg" aria-hidden>
        <span className="cypher-page__blob cypher-page__blob--1" />
        <span className="cypher-page__blob cypher-page__blob--2" />
        <span className="cypher-page__blob cypher-page__blob--3" />
        <span className="cypher-page__grid" />
        <span className="cypher-page__ring cypher-page__ring--1" />
        <span className="cypher-page__ring cypher-page__ring--2" />
      </div>

      <section className="cypher-page__hero container-padding">
        <div className="cypher-page__hero-inner">
          <div className="cypher-page__hero-copy">
            <span className="cypher-page__live">
              <span className="cypher-page__live-dot" aria-hidden />
              {CYPHER_COPY.eyebrow}
            </span>
            <h1 className="cypher-page__title">{CYPHER_COPY.title}</h1>
            <p className="cypher-page__subtitle">{CYPHER_COPY.subtitle}</p>
            <div className="cypher-page__hero-actions">
              <a href="#cypher-tracks" className="cypher-page__btn cypher-page__btn--primary">
                Jump into a beat
              </a>
              {(role === 'producer' || role === 'producer_rapper') && (
                <Link to="/upload" className="cypher-page__btn cypher-page__btn--ghost">
                  Upload a challenge beat
                </Link>
              )}
              {role === 'listener' && (
                <a href="#cypher-drops" className="cypher-page__btn cypher-page__btn--ghost">
                  Hear recent drops
                </a>
              )}
            </div>
          </div>

          <div className="cypher-page__hero-visual" aria-hidden>
            <div className="cypher-page__booth">
              <span className="cypher-page__booth-mic" />
              <span className="cypher-page__booth-wave" />
              <span className="cypher-page__booth-wave cypher-page__booth-wave--delay" />
              <span className="cypher-page__booth-label">OPEN MIC</span>
            </div>
          </div>
        </div>
      </section>

      <section className="cypher-page__steps container-padding" aria-labelledby="cypher-how">
        <h2 id="cypher-how" className="cypher-page__section-title">
          How it works
        </h2>
        <ol className="cypher-page__steps-grid">
          {CYPHER_STEPS.map(item => (
            <li key={item.step} className="cypher-page__step">
              <span className="cypher-page__step-num">{item.step}</span>
              <h3 className="cypher-page__step-title">{item.title}</h3>
              <p className="cypher-page__step-body">{item.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section
        id="cypher-tracks"
        className="cypher-page__tracks container-padding"
        aria-labelledby="cypher-tracks-heading"
      >
        <div className="cypher-page__section-head">
          <h2 id="cypher-tracks-heading" className="cypher-page__section-title">
            Challenge tracks
          </h2>
          <p className="cypher-page__section-lead">
            Featured cypher beats only. Preview, then drop your bars in the booth.
          </p>
        </div>

        {loading && (
          <div className="cypher-page__loading">
            <div className="cypher-page__spinner" aria-label="Loading" />
            <p>Loading the session…</p>
          </div>
        )}

        {error && (
          <div className="cypher-page__error">
            <p>{error}</p>
            <button type="button" className="cypher-page__btn cypher-page__btn--ghost" onClick={() => refresh()}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && beats.length === 0 && (
          <div className="cypher-page__empty">
            <h3>No tracks in the cypher yet</h3>
            <p>
              Producers can upload a public beat and choose <strong>Cypher challenge</strong> as the
              beat purpose to list it here.
            </p>
            {(role === 'producer' || role === 'producer_rapper') && (
              <Link to="/upload" className="cypher-page__btn cypher-page__btn--primary">
                Upload a challenge beat
              </Link>
            )}
          </div>
        )}

        {!loading && !error && beats.length > 0 && (
          <div className="cypher-page__track-list">
            {beats.map((beat, i) => (
              <CypherTrackCard key={beat.id} beat={beat} index={i} onRecorded={refresh} />
            ))}
          </div>
        )}
      </section>

      <section className="cypher-page__drops container-padding" aria-labelledby="cypher-drops">
        <div className="cypher-page__section-head">
          <h2 id="cypher-drops" className="cypher-page__section-title">
            Recent drops
          </h2>
          <p className="cypher-page__section-lead">
            Fresh verses from this cypher session. Vote on what hits.
          </p>
        </div>

        {!loading && verses.length === 0 && (
          <div className="cypher-page__empty cypher-page__empty--soft">
            <p>No verses on these tracks yet — be the first to drop.</p>
          </div>
        )}

        {verses.length > 0 && (
          <div className="cypher-page__verse-grid">
            {verses.map(verse => (
              <VerseCard key={verse.id} verse={verse} variant="dark" onVote={refresh} />
            ))}
          </div>
        )}
      </section>

      <section className="cypher-page__footer-cta container-padding">
        <div className="cypher-page__footer-card">
          <h2>Ready for the booth?</h2>
          <p>Stack the cypher, build your portfolio, and let the community decide what hits.</p>
          <div className="cypher-page__hero-actions">
            <a href="#cypher-tracks" className="cypher-page__btn cypher-page__btn--primary">
              Pick a beat
            </a>
            <Link to="/home" className="cypher-page__btn cypher-page__btn--ghost">
              Back to feed
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CypherChallenge;
